import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useI18n } from '../lib/use-i18n.js';
import { LocaleLink as Link, LocaleNavLink as NavLink } from '../lib/locale-link.js';
import { prefixedLocales, stripLocalePrefix } from '../lib/locale-routing.js';
import { dashboardLoginUrl } from '../features/signup/redirect-to-dashboard.js';
import { LocaleSwitcher } from './locale-switcher.js';
import { LocaleSuggestionBanner } from './locale-suggestion-banner.js';
import { ThemeToggle } from './theme-toggle.js';

/**
 * Marketing-site shell — glass sticky header + cinematic dark footer.
 *
 * Header chrome (right-aligned): locale switcher · theme toggle · operator
 * "Sign in" (a DIRECT link to the dashboard origin, `env.dashboardOrigin`, via
 * `dashboardLoginUrl()`) · the trial CTA. Below 720px the secondary nav + the
 * two account links collapse into a hamburger drawer. The header background,
 * borders and text all read theme tokens so the whole bar re-skins with the
 * light/dark toggle.
 *
 * The agent "Sign in" lives on the become-an-agent surface (not here) — there is
 * deliberately no combined chooser page (owner amendment, replaces PR #161).
 */
export function MarketingShell({ children }: { readonly children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--color-bg)',
        color: 'var(--color-on-surface)',
      }}
    >
      <LocaleSuggestionBanner />
      <MarketingHeader />
      <main style={{ flex: 1 }}>{children}</main>
      <MarketingFooter />
    </div>
  );
}

function MarketingHeader() {
  const brand = usePlatformBrand();
  const { t } = useI18n();
  const { pathname } = useLocation();
  const basePath = stripLocalePrefix(
    pathname,
    prefixedLocales(brand.supportedLocales, brand.defaultLocale),
  );
  const onSignupPage = basePath === '/signup';
  const isCompact = useIsCompact();
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  return (
    <header
      className="glass"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        borderBottom: '1px solid var(--glass-border)',
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: '0 auto',
          padding: 'var(--space-3) var(--space-4)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          minWidth: 0,
        }}
      >
        <Link
          to="/"
          className="lift"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            // The wordmark is the site's home link; it measured 26px tall.
            minHeight: 'var(--touch-target, 44px)',
            gap: '0.45rem',
            fontSize: '1.05rem',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: 'var(--color-on-surface)',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
          aria-label={brand.name}
        >
          <BrandBolt />
          {brand.shortName}
        </Link>

        {!isCompact ? (
          <nav style={{ display: 'flex', gap: 'var(--space-5)', marginLeft: 'var(--space-4)' }}>
            <NavLinkStyled to="/features" label={t('marketing.nav.features')} />
            <NavLinkStyled to="/pricing" label={t('marketing.nav.pricing')} />
            <NavLinkStyled to="/become-an-agent" label={t('marketing.nav.forAgents')} />
          </nav>
        ) : null}

        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            gap: 'var(--space-2)',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <LocaleSwitcher />
          <ThemeToggle />
          {!isCompact ? (
            <a
              href={dashboardLoginUrl()}
              className="lift"
              style={{
                fontSize: 'var(--text-meta)',
                fontWeight: 600,
                color: 'var(--color-on-surface-muted)',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                padding: '0.5em 0.6em',
                minHeight: '2.5rem',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {t('marketing.nav.signIn')}
            </a>
          ) : null}
          {!isCompact && !onSignupPage ? (
            <Link
              to="/signup"
              className="lift"
              style={{
                background: 'var(--grad-lume)',
                color: '#ffffff',
                padding: '0.5em 1.1em',
                borderRadius: 'var(--radius-pill)',
                fontSize: 'var(--text-meta)',
                fontWeight: 700,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
                minHeight: '2.5rem',
                display: 'inline-flex',
                alignItems: 'center',
                boxShadow: 'var(--shadow-glow)',
              }}
            >
              {t('marketing.cta.startTrialShort')}
            </Link>
          ) : null}
          {isCompact ? (
            <button
              type="button"
              className="lift"
              onClick={() => setNavOpen((v) => !v)}
              aria-label={t('marketing.nav.menu')}
              aria-expanded={navOpen}
              style={{
                minWidth: '2.75rem',
                minHeight: '2.75rem',
                width: '2.75rem',
                height: '2.75rem',
                borderRadius: '999px',
                border: '1px solid var(--glass-border)',
                background: 'var(--glass-bg)',
                color: 'var(--color-on-surface)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
                flexShrink: 0,
              }}
            >
              <HamburgerIcon open={navOpen} />
            </button>
          ) : null}
        </div>
      </div>

      {isCompact && navOpen ? (
        <nav
          style={{
            display: 'grid',
            gap: 'var(--space-1)',
            padding: 'var(--space-3) var(--space-4) var(--space-4)',
            borderTop: '1px solid var(--glass-border)',
            background: 'var(--color-surface)',
          }}
        >
          <MobileNavLink to="/features" label={t('marketing.nav.features')} />
          <MobileNavLink to="/pricing" label={t('marketing.nav.pricing')} />
          <MobileNavLink to="/become-an-agent" label={t('marketing.nav.forAgents')} />
          <MobileNavLink to="/become-a-recruiter" label={t('marketing.nav.forRecruiters')} />
          <a
            href={dashboardLoginUrl()}
            style={{
              padding: '0.75em 0.875em',
              minHeight: '2.75rem',
              display: 'flex',
              alignItems: 'center',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--color-on-surface)',
              fontSize: 'var(--text-body)',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            {t('marketing.nav.signIn')}
          </a>
          {!onSignupPage ? (
            <Link
              to="/signup"
              style={{
                marginTop: 'var(--space-2)',
                background: 'var(--grad-lume)',
                color: '#ffffff',
                padding: '12px 18px',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-body)',
                fontWeight: 700,
                textDecoration: 'none',
                textAlign: 'center',
                boxShadow: 'var(--shadow-glow)',
              }}
            >
              {t('marketing.cta.startTrial')}
            </Link>
          ) : null}
        </nav>
      ) : null}
    </header>
  );
}

function BrandBolt() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" fill="var(--kilat-lume)" />
    </svg>
  );
}

function NavLinkStyled({ to, label }: { readonly to: string; readonly label: string }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        color: isActive ? 'var(--color-on-surface)' : 'var(--color-on-surface-muted)',
        fontSize: 'var(--text-meta)',
        fontWeight: isActive ? 700 : 500,
        textDecoration: 'none',
        whiteSpace: 'nowrap',
      })}
    >
      {label}
    </NavLink>
  );
}

function MobileNavLink({ to, label }: { readonly to: string; readonly label: string }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        padding: '0.75em 0.875em',
        minHeight: '2.75rem',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 'var(--radius-sm)',
        color: isActive ? 'var(--kilat-lume)' : 'var(--color-on-surface)',
        background: isActive ? 'var(--kilat-lume-soft)' : 'transparent',
        fontSize: 'var(--text-body)',
        lineHeight: 1.35,
        fontWeight: isActive ? 600 : 500,
        textDecoration: 'none',
      })}
    >
      {label}
    </NavLink>
  );
}

function HamburgerIcon({ open }: { readonly open: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        d={open ? 'M6 6 L18 18 M18 6 L6 18' : 'M4 7 H20 M4 12 H20 M4 17 H20'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/**
 * 720px breakpoint via matchMedia, resolved on the FIRST render.
 *
 * This used to start `false` and correct itself in an effect — "SSR-safe
 * (defaults to desktop pre-mount)". Desktop is the wrong default for a site
 * most people open on a phone: the first paint at 390px laid out the full
 * desktop header, which measures 656px, so every mobile visitor got a
 * horizontally overflowing header until React hydrated and collapsed it.
 *
 * It surfaced as a "flaky" locale × width test — passing on retry, because by
 * the retry the page was warm enough to hydrate before the assertion. A flake
 * that only appears on a cold load is a real defect that real cold loads have.
 *
 * A lazy initialiser runs DURING the first render, before paint, so in a
 * browser the header is never laid out wrong. Where there is no `window`
 * (the prerender step), it still falls back to desktop — a prerenderer has no
 * viewport to answer with, and the CSS guard in `globals.css` covers that
 * window until hydration.
 */
function useIsCompact(): boolean {
  const [compact, setCompact] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 720px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 720px)');
    const onChange = () => setCompact(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return compact;
}

function MarketingFooter() {
  const brand = usePlatformBrand();
  const { locale, t, platformBrandCopy } = useI18n();
  const year = new Date().getFullYear();
  return (
    <footer
      className="on-dark"
      style={{
        marginTop: 'clamp(3rem, 6vw, 5rem)',
        background: 'var(--hero-ground)',
        borderTop: '1px solid var(--glass-border)',
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: '0 auto',
          padding: 'clamp(2.5rem, 5vw, 4rem) var(--space-6) var(--space-8)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 170px), 1fr))',
          gap: 'var(--space-8)',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        <div>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.45rem',
              fontSize: '1.15rem',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: 'var(--color-on-surface)',
            }}
          >
            <BrandBolt />
            {brand.shortName}
          </span>
          <p style={{ marginTop: 'var(--space-3)', lineHeight: 1.5, maxWidth: '22rem' }}>
            {platformBrandCopy('pitchOneLiner')}
          </p>
          <p style={{ marginTop: 'var(--space-2)', lineHeight: 1.5 }}>
            {brand.tagline[locale] ?? brand.tagline.en}
          </p>
        </div>
        <FooterCol heading={t('marketing.footer.product')}>
          <FooterLink to="/features">{t('marketing.footer.features')}</FooterLink>
          <FooterLink to="/pricing">{t('marketing.footer.pricing')}</FooterLink>
          <FooterLink to="/help">{t('marketing.footer.helpCenter')}</FooterLink>
          <FooterLink to="/signup">{t('marketing.footer.startTrial')}</FooterLink>
        </FooterCol>
        <FooterCol heading={t('marketing.footer.salesAgents')}>
          <FooterLink to="/become-an-agent">{t('marketing.footer.earnCommission')}</FooterLink>
          <FooterLink to="/become-an-agent/signup">{t('marketing.footer.apply')}</FooterLink>
        </FooterCol>
        <FooterCol heading={t('marketing.footer.company')}>
          <FooterLink to="/about">{t('marketing.footer.about')}</FooterLink>
          <FooterLink to="/contact">{t('marketing.footer.contact')}</FooterLink>
          {brand.contact.email ? (
            <FooterAnchor href={`mailto:${brand.contact.email}`}>
              {brand.contact.email}
            </FooterAnchor>
          ) : null}
          {brand.contact.whatsapp ? (
            <FooterAnchor href={`https://wa.me/${brand.contact.whatsapp.replace(/[^0-9]/g, '')}`}>
              {t('marketing.footer.whatsapp')}
            </FooterAnchor>
          ) : null}
        </FooterCol>
        <FooterCol heading={t('marketing.footer.legal')}>
          <FooterLink to="/legal/tos">{t('marketing.footer.tos')}</FooterLink>
          <FooterLink to="/legal/privacy">{t('marketing.footer.privacy')}</FooterLink>
          <FooterLink to="/legal/agent-agreement">
            {t('marketing.footer.agentAgreement')}
          </FooterLink>
        </FooterCol>
      </div>
      <div
        style={{
          borderTop: '1px solid var(--glass-border)',
          padding: 'var(--space-4) var(--space-6)',
          textAlign: 'center',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {t('marketing.footer.rights', {
          year,
          company: brand.legal.companyName ?? brand.name,
        })}
      </div>
    </footer>
  );
}

function FooterCol({
  heading,
  children,
}: {
  readonly heading: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div>
      <p style={{ margin: 0, fontWeight: 700, color: 'var(--color-on-surface)' }}>{heading}</p>
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 'var(--space-3) 0 0 0',
          display: 'grid',
          gap: 'var(--space-2)',
        }}
      >
        {children}
      </ul>
    </div>
  );
}

function FooterLink({ to, children }: { readonly to: string; readonly children: React.ReactNode }) {
  return (
    <li>
      <Link to={to} style={{ color: 'inherit', textDecoration: 'none' }} className="footer-link">
        {children}
      </Link>
    </li>
  );
}

function FooterAnchor({
  href,
  children,
}: {
  readonly href: string;
  readonly children: React.ReactNode;
}) {
  return (
    <li>
      <a href={href} style={{ color: 'inherit', textDecoration: 'none' }} className="footer-link">
        {children}
      </a>
    </li>
  );
}
